/**
 * COLORS (2026-10-05, Matt: "We need to delineate between a material finish and a color … The
 * paint colors should be modifiers, not actual textures … an organized system for managing and
 * applying material finishes that is separate from applying colors. They shouldn't be mixed
 * lists"). Decisions, Matt same day:
 *   - the color list is FIRM-WIDE ("we want to discourage projects from not lending their
 *     experience to the library") — a color made in a project goes straight to the firm list;
 *   - two kinds: PAINTS and STAINS (a stain is a color, not a finish);
 *   - the brush works by wall face, roof plane and whole member;
 *   - no color schedule sheet — colors coordinate with the AlderSPEC schedules instead.
 *
 * A color is never a material. It is a modifier on a finish: what a surface SHOWS is
 *   1. the color brushed on that surface, else
 *   2. the project's STANDARD color for that finish (set once per project), else
 *   3. the finish material's own color.
 * Only the 3D view shows it (elevation linework stays colorless — the 3D backdrop is how an
 * elevation shows color). The renderer reads a tinted pseudo material id `tint:<hex>:<mat>`
 * (the finish's gloss, the color's color); brick/stone/shingle shades are shades of the tint.
 *
 * Storage, all in the settings map (synced + undoable, no new top map):
 *   'colors'          Record<colorId, ColorRecord> — the firm list mirrored on open, plus any
 *                     color made here and not yet confirmed by the firm library (`firmPending`)
 *   'colorStandards'  Record<finishMaterialId, colorRef> — this project's standards (a color id,
 *                     a palette role `role:<id>`, or 'natural' = the finish's own color)
 *   'palette'         Record<roleId, colorId> — the project palette (see below)
 */
import { maps, transact, getMaterial, recordToYMap, docVersion, type ModelDoc } from './doc'
import { colorFits, substrateOf, SUBSTRATES } from './finishCompat'
import type { MaterialId } from './types'
import { LIGHT_BODY_MATERIALS } from './categories/fixture/lightStyles'
import { PLUMBING_MATERIALS } from './categories/object/plumbingStyles'
import { nanoid } from 'nanoid'

/**
 * FINISH COMPATIBILITY (2026-10-07, Matt: "we can't apply inappropriate finishes to materials"):
 * five kinds of color, each going only on finishes that can wear it (finishCompat.ts):
 *   paint     house paints (Sherwin-Williams)
 *   stain     wood stains (Minwax interior, WoodScapes exterior)
 *   integral  colored-through stucco (Omega)
 *   coating   factory metal coatings — RAL, PVDF / powder coat
 *   factory   a manufacturer's own product colors, tied to its product LINES (Millboard, GAF…)
 *   bare      a bare metal — copper, zinc, steel (BARE_METALS, built in, never stored): the
 *             metal is the look, so sheet metal can be zinc or copper or RAL-coated
 */
export type ColorKind = 'paint' | 'stain' | 'integral' | 'coating' | 'factory' | 'bare'
export const COLOR_KINDS: { value: ColorKind; label: string; plural: string }[] = [
  { value: 'paint', label: 'Paint', plural: 'Paints' },
  { value: 'stain', label: 'Stain', plural: 'Stains' },
  { value: 'integral', label: 'Integral Color', plural: 'Integral Colors (Stucco)' },
  { value: 'bare', label: 'Bare Metal', plural: 'Bare Metals' },
  { value: 'coating', label: 'Metal Coating', plural: 'Metal Coatings (RAL)' },
  { value: 'factory', label: 'Factory Finish', plural: 'Factory Finishes' },
]
const isKind = (v: unknown): v is ColorKind => COLOR_KINDS.some((k) => k.value === v)

export interface ColorRecord {
  id: string
  name: string
  kind: ColorKind
  /** '#rrggbb' */
  hex: string
  /** e.g. 'Benjamin Moore' */
  maker?: string
  /** the maker's color number, e.g. 'OC-17' */
  code?: string
  /** factory colors: the product lines it comes in (finishCompat.ts PRODUCT_LINES ids) */
  lines?: string[]
  /** made in this model; the firm library hasn't confirmed it yet (the page retries) */
  firmPending?: boolean
  retired?: boolean
}

const KEY = 'colors'
const STD_KEY = 'colorStandards'
const HEX = /^#[0-9a-f]{6}$/i

export const newColorId = (): string => nanoid(12)

export function normalizeColor(v: unknown): ColorRecord | null {
  const o = (v || {}) as Partial<ColorRecord>
  if (typeof o.id !== 'string' || !o.id || typeof o.name !== 'string' || !o.name.trim()) return null
  const hex = typeof o.hex === 'string' && HEX.test(o.hex) ? o.hex.toLowerCase() : null
  if (!hex) return null
  return {
    id: o.id, name: o.name.trim(), kind: isKind(o.kind) ? o.kind : 'paint', hex,
    ...(Array.isArray(o.lines) && o.lines.length ? { lines: o.lines.filter((x) => typeof x === 'string' && x) } : {}),
    ...(typeof o.maker === 'string' && o.maker.trim() ? { maker: o.maker.trim() } : {}),
    ...(typeof o.code === 'string' && o.code.trim() ? { code: o.code.trim() } : {}),
    ...(o.firmPending ? { firmPending: true } : {}),
    ...(o.retired ? { retired: true } : {}),
  }
}

function storedColorMap(model: ModelDoc): Record<string, ColorRecord> {
  const raw = maps(model).settings.get(KEY) as Record<string, unknown> | undefined
  const out: Record<string, ColorRecord> = {}
  for (const [id, v] of Object.entries(raw || {})) {
    const c = normalizeColor(v)
    if (c && c.id === id && c.kind !== 'bare') out[id] = c
  }
  return out
}
/** the stored colors plus the built-in bare metals (`bare:<key>`).
 *  3D PERF round (2026-10-13): memoized per doc version — resolving one element reads
 *  this several times (surfaceHex per face / opening / member), and each read used to
 *  re-normalize the whole color library; 661 elements × that was most of a 0.7 s
 *  resolve. Same contract as the kernel read cache: fresh inside an open transaction. */
const colorMapCache = new WeakMap<object, { ver: number; map: Record<string, ColorRecord> }>()
function colorMap(model: ModelDoc): Record<string, ColorRecord> {
  const build = (): Record<string, ColorRecord> => {
    const out = storedColorMap(model)
    for (const b of BARE_METALS) out[bareMetalRef(b.key)] = { id: bareMetalRef(b.key), name: b.label, kind: 'bare', hex: b.color }
    return out
  }
  if ((model.ydoc as unknown as { _transaction?: unknown })._transaction != null) return build()
  const ver = docVersion(model)
  const c = colorMapCache.get(model.ydoc)
  if (c && c.ver === ver) return c.map
  const map = build()
  colorMapCache.set(model.ydoc, { ver, map })
  return map
}

/** every live color, grouped by kind (paints, stains, integral, coatings, factory), then by name */
export function listColors(model: ModelDoc): ColorRecord[] {
  const order = (k: ColorKind) => COLOR_KINDS.findIndex((x) => x.value === k)
  return Object.values(colorMap(model))
    .filter((c) => !c.retired)
    .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : order(a.kind) - order(b.kind)))
}

/** a color by id — retired ones included (an applied color keeps showing) */
export function getColor(model: ModelDoc, id: string | null | undefined): ColorRecord | null {
  return (id && colorMap(model)[id]) || null
}

/** add or update colors (one transact = one undo step) -> how many changed */
export function putColors(model: ModelDoc, records: ColorRecord[]): number {
  const cur = storedColorMap(model)
  let n = 0
  const next = { ...cur }
  for (const r of records) {
    const c = normalizeColor(r)
    if (!c || c.kind === 'bare') continue
    if (JSON.stringify(cur[c.id]) === JSON.stringify(c)) continue
    next[c.id] = c
    n++
  }
  if (n) transact(model, () => { maps(model).settings.set(KEY, next) })
  return n
}

/**
 * On open: mirror the firm color list into the model (add + update; a color the firm retired
 * stays readable for surfaces already wearing it). A local color the firm now has is no longer
 * pending. -> how many changed
 */
export function mirrorFirmColors(model: ModelDoc, firm: unknown[] | null | undefined): number {
  const recs = (firm || []).map(normalizeColor).filter((c): c is ColorRecord => !!c)
  return putColors(model, recs.map((c) => ({ ...c, firmPending: undefined })))
}

/** colors made here that the firm library hasn't confirmed yet */
export function pendingFirmColors(model: ModelDoc): ColorRecord[] {
  return Object.values(storedColorMap(model)).filter((c) => c.firmPending)
}

// ---------- project standards ----------

export function getColorStandards(model: ModelDoc): Record<string, string> {
  const raw = maps(model).settings.get(STD_KEY) as Record<string, unknown> | undefined
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(raw || {})) if (typeof v === 'string' && v) out[k] = v
  return out
}

/** set (or clear, with null) this project's standard color for a finish material */
export function setColorStandard(model: ModelDoc, materialId: string, colorId: string | null): void {
  const next = { ...getColorStandards(model) }
  if (colorId) next[materialId] = colorId
  else delete next[materialId]
  transact(model, () => { maps(model).settings.set(STD_KEY, next) })
}

// ---------- the palette: color ROLES ----------
//
// PROJECT STYLE phase 1 (2026-10-06, Matt: "We set the colors in our palette of colors and
// apply by choosing from what's in the palette based on role - not every available color in
// the library which then allows our design to potentially come uncoordinated"). The project
// names a color for each ROLE; a surface brushed with a role (`role:<id>`) follows the role
// live, so re-coloring the Accent re-colors every accent surface at once. Some surfaces take a
// role by default (an exterior painted wall face → Field) — doc.ts decides which.
//
// Storage: settings 'palette' = Record<roleId, colorId>.

export type PaletteRoleId =
  | 'field' | 'accent' | 'trim' | 'winExt' | 'winInt' | 'frontDoor' | 'metals'
  | 'intWalls' | 'intTrim' | 'cabinets' | 'boxBeams'

export interface PaletteRole { id: PaletteRoleId; label: string; hint: string; group: 'Exterior' | 'Windows & Doors' | 'Interior' }

export const PALETTE_ROLES: PaletteRole[] = [
  { id: 'field', label: 'Field', group: 'Exterior', hint: 'The main exterior wall color. Painted siding and stucco follow it unless set otherwise.' },
  { id: 'accent', label: 'Accent', group: 'Exterior', hint: 'A second exterior color. Brush it where you want it: a gable, a bay, a band.' },
  { id: 'trim', label: 'Trim', group: 'Exterior', hint: 'Fascia, soffits (and the decking under open eaves) and barge boards follow it.' },
  { id: 'metals', label: 'Metals', group: 'Exterior', hint: 'Railings, gutters, downspouts, flashing. Brush it where you want it.' },
  { id: 'winExt', label: 'Window & Door Exterior', group: 'Windows & Doors', hint: 'Window and door frames outside, in exterior walls.' },
  { id: 'winInt', label: 'Window & Door Interior', group: 'Windows & Doors', hint: 'Window and door frames inside, when the frame system has two colors (aluminum-clad, wood). Not set = the natural wood.' },
  { id: 'frontDoor', label: 'Front Door', group: 'Windows & Doors', hint: 'The entry door itself (its slab); its frame keeps the Window & Door colors. Brush an entry door to give it its own.' },
  { id: 'intWalls', label: 'Interior Walls', group: 'Interior', hint: 'Painted interior wall faces follow it.' },
  { id: 'intTrim', label: 'Interior Trim', group: 'Interior', hint: 'Interior casings and base follow it with the doors step.' },
  { id: 'cabinets', label: 'Cabinets', group: 'Interior', hint: 'Painted cabinets follow it.' },
  // DECORATIVE BOX BEAMS (2026-10-08, Matt: "they default to a simple wood material with the
  // assigned project color/finish to them")
  { id: 'boxBeams', label: 'Box Beams', group: 'Interior', hint: 'Decorative box beams on ceilings follow it (a paint or a stain). Not set = the wood’s own color. Brush a ceiling’s beams to give them their own.' },
]

const PALETTE_KEY = 'palette'
const ROLE_PREFIX = 'role:'
/** a standard that opts a finish out of every role default: its own color */
export const NATURAL_REF = 'natural'

export const roleRef = (id: PaletteRoleId): string => ROLE_PREFIX + id
export function roleOfRef(ref: unknown): PaletteRoleId | null {
  if (typeof ref !== 'string' || !ref.startsWith(ROLE_PREFIX)) return null
  const id = ref.slice(ROLE_PREFIX.length)
  return PALETTE_ROLES.some((r) => r.id === id) ? (id as PaletteRoleId) : null
}
export const getPaletteRole = (id: PaletteRoleId | null | undefined): PaletteRole | null =>
  PALETTE_ROLES.find((r) => r.id === id) ?? null

export function getPalette(model: ModelDoc): Partial<Record<PaletteRoleId, string>> {
  const raw = maps(model).settings.get(PALETTE_KEY) as Record<string, unknown> | undefined
  const out: Partial<Record<PaletteRoleId, string>> = {}
  for (const r of PALETTE_ROLES) {
    const v = raw?.[r.id]
    if (typeof v === 'string' && v) out[r.id] = v
  }
  return out
}

/** set (or clear, with null) the color for a role */
export function setPaletteColor(model: ModelDoc, role: PaletteRoleId, colorId: string | null): void {
  const next = { ...getPalette(model) }
  if (colorId) next[role] = colorId
  else delete next[role]
  transact(model, () => { maps(model).settings.set(PALETTE_KEY, next) })
}

/** a color reference — a color id, or `role:<id>` through the palette — to its color */
export function resolveColorRef(model: ModelDoc, ref: unknown): ColorRecord | null {
  if (typeof ref !== 'string' || !ref || ref === NATURAL_REF) return null
  const role = roleOfRef(ref)
  if (role) return getColor(model, getPalette(model)[role])
  return getColor(model, ref)
}

/** 'Field · OC-17 White Dove (Benjamin Moore)' / 'Field (not set)' / a color label */
export function colorRefLabel(model: ModelDoc, ref: unknown): string {
  if (ref === NATURAL_REF) return "Finish's own color"
  const role = roleOfRef(ref)
  if (role) {
    const c = resolveColorRef(model, ref)
    return getPaletteRole(role)!.label + (c ? ' · ' + colorLabel(c) : ' (not set)')
  }
  const c = typeof ref === 'string' ? getColor(model, ref) : null
  return c ? colorLabel(c) : ''
}

// ---------- resolution ----------

/**
 * The hex a surface shows over its finish material:
 *   1. the brushed color (a color, or a palette role), else
 *   2. the project standard for that finish (a color, a role, or "own color"), else
 *   3. the surface's default ROLE (`roleDefault`, chosen by doc.ts for paintable finishes only), else
 *   4. null — the material's own color.
 */
export function surfaceHex(
  model: ModelDoc, materialId: string | null | undefined, brushed: unknown, roleDefault?: PaletteRoleId | null,
): string | null {
  const own = resolveColorRef(model, brushed)
  if (own) return colorToken(own)
  if (!materialId) return null
  const std = getColorStandards(model)[materialId]
  if (std === NATURAL_REF) return null
  const s = resolveColorRef(model, std)
  if (s) return colorToken(s)
  if (roleDefault) {
    // FINISH COMPATIBILITY: a role reaches a finish only if the finish can wear that color
    const rc = getColor(model, getPalette(model)[roleDefault])
    if (!rc) return null
    return colorFits(getMaterial(model, materialId as MaterialId), rc).ok ? colorToken(rc) : null
  }
  return null
}

// ---------- bare metals (the Metals role) ----------
//
// BARE METALS (2026-10-07, Matt: "we don't have bare metal finishes in the list, like copper,
// zinc, galvanized metal, stainless steel. Those need to be added."). A bare metal is a FINISH,
// not a color — the metal is its own color — so the Metals role can name one (`bare:<key>` in
// the palette) and the project's gutters, downspouts, railings and flashing are made of it.
// Firm-standard materials (stable firm ids, so the firm library and a model adopt the same
// record); picking one adds it to a model that doesn't have it yet.

export interface BareMetal { key: string; firmId: string; label: string; name: string; color: string; render: { metalness: number; roughness: number } }
export const BARE_METALS: BareMetal[] = [
  { key: 'copper', firmId: 'bareCopper01', label: 'Copper', name: 'Bare Metal — Copper', color: '#b87333', render: { metalness: 1, roughness: 0.35 } },
  { key: 'zinc', firmId: 'bareZinc01', label: 'Zinc', name: 'Bare Metal — Zinc', color: '#8a8f93', render: { metalness: 1, roughness: 0.55 } },
  { key: 'galvanized', firmId: 'bareGalv01', label: 'Galvanized Steel', name: 'Bare Metal — Galvanized Steel', color: '#a3a8ab', render: { metalness: 1, roughness: 0.45 } },
  { key: 'stainless', firmId: 'bareStain01', label: 'Stainless Steel', name: 'Bare Metal — Stainless Steel', color: '#c5c8ca', render: { metalness: 1, roughness: 0.3 } },
  // the sheet-metal panel looks (FINISH CONSOLIDATION, 2026-10-07: these were separate
  // "Metal Panel Siding — …" finishes; now one panel finish wearing a bare metal)
  { key: 'blackened', firmId: 'bareBlkStl01', label: 'Blackened Steel', name: 'Bare Metal — Blackened Steel', color: '#2e2d2b', render: { metalness: 1, roughness: 0.5 } },
  { key: 'hotRolled', firmId: 'bareHotRol01', label: 'Hot Rolled Steel', name: 'Bare Metal — Hot Rolled Steel', color: '#4b4a48', render: { metalness: 1, roughness: 0.6 } },
  { key: 'weathering', firmId: 'bareWeathr01', label: 'Weathering Steel', name: 'Bare Metal — Weathering Steel', color: '#7b4a2c', render: { metalness: 0.6, roughness: 0.8 } },
  // LIGHT STYLES (2026-10-07): the light-fixture metals — what a lantern, a sconce or a
  // chandelier is actually sold in (the brush puts them on a fixture's metal parts)
  { key: 'brass', firmId: 'bareBrass01', label: 'Polished Brass', name: 'Bare Metal — Polished Brass', color: '#c9a24d', render: { metalness: 1, roughness: 0.25 } },
  { key: 'agedBrass', firmId: 'bareAgedBrs01', label: 'Aged Brass', name: 'Bare Metal — Aged Brass', color: '#8f7140', render: { metalness: 1, roughness: 0.5 } },
  { key: 'bronze', firmId: 'bareBronze01', label: 'Oil-Rubbed Bronze', name: 'Bare Metal — Oil-Rubbed Bronze', color: '#3b2b20', render: { metalness: 0.8, roughness: 0.55 } },
  { key: 'nickel', firmId: 'bareNickel01', label: 'Polished Nickel', name: 'Bare Metal — Polished Nickel', color: '#c8c3b6', render: { metalness: 1, roughness: 0.2 } },
  { key: 'verdigris', firmId: 'bareVerdig01', label: 'Aged Copper (Verdigris)', name: 'Bare Metal — Aged Copper (Verdigris)', color: '#5f8a76', render: { metalness: 0.3, roughness: 0.75 } },
]
/** the token a bare metal leaves in a derived material id: `metal:<key>:<mat>` (sceneStore
 *  renders it as the metal — its color AND its metallic look; a `tint:` is paint) */
export const metalToken = (key: string): string => 'metal:' + key
export function bareMetalOfToken(token: unknown): BareMetal | null {
  return typeof token === 'string' && token.startsWith('metal:') ? BARE_METALS.find((b) => 'metal:' + b.key === token) ?? null : null
}
/** what a color leaves on a surface: its hex, or a bare metal's token */
export const colorToken = (c: ColorRecord): string => (c.kind === 'bare' ? metalToken(c.id.slice(BARE_PREFIX.length)) : c.hex)
const BARE_PREFIX = 'bare:'
export const bareMetalRef = (key: string): string => BARE_PREFIX + key
export function bareMetalOfRef(ref: unknown): BareMetal | null {
  if (typeof ref !== 'string' || !ref.startsWith(BARE_PREFIX)) return null
  return BARE_METALS.find((b) => b.key === ref.slice(BARE_PREFIX.length)) ?? null
}
/** the material record a bare metal is, as stored in the firm library and a model */
export function bareMetalRecord(b: BareMetal): Record<string, unknown> {
  return {
    id: b.firmId, firmId: b.firmId, name: b.name, color: b.color, category: 'Metal',
    surface: { color: b.color }, cut: { pattern: 'solid', fgColor: '#5a5e61', bgColor: '#e4e2de' },
    render: b.render, substrate: 'naturalMetal',
  }
}
/** the model's material for a bare metal (by firm id, then name), or null */
export function bareMetalMaterialId(model: ModelDoc, b: BareMetal): string | null {
  for (const [id, m] of maps(model).materials) {
    const firmId = m.get('firmId'), name = m.get('name')
    if (firmId === b.firmId || id === b.firmId || name === b.name) return id
  }
  return null
}
/** make sure the model has this bare metal's material; returns its id */
export function ensureBareMetalMaterial(model: ModelDoc, b: BareMetal): string {
  const have = bareMetalMaterialId(model, b)
  if (have) return have
  transact(model, () => { maps(model).materials.set(b.firmId, recordToYMap(bareMetalRecord(b))) })
  return b.firmId
}
/**
 * LIGHT STYLES (2026-10-07): make sure the model has the two Light Fixture finish materials (the
 * metal and plaster bodies every styled light's color is resolved over — lightStyles.ts). Run
 * on open with the other idempotent fixes; returns how many it added (0 = already there).
 */
export function ensureLightFixtureMaterials(model: ModelDoc): number {
  let n = 0
  const have = (rec: Record<string, unknown>) => {
    for (const [id, m] of maps(model).materials) if (id === rec.id || m.get('firmId') === rec.firmId || m.get('name') === rec.name) return true
    return false
  }
  // PLUMBING STYLES (2026-10-07): the plumbing fixtures' metal and body finishes too
  const missing = [...Object.values(LIGHT_BODY_MATERIALS), ...Object.values(PLUMBING_MATERIALS)].filter((r) => !have(r))
  if (missing.length) transact(model, () => { for (const r of missing) { maps(model).materials.set(String(r.id), recordToYMap(r)); n++ } })
  return n
}

/** the Metals role when it names a bare metal: that metal's material id (null = a color, unset,
 *  or the material isn't in the model) */
export function metalsBareMaterial(model: ModelDoc): string | null {
  const b = bareMetalOfRef(getPalette(model).metals)
  return b ? bareMetalMaterialId(model, b) : null
}
/** can a color be the Metals role? — what a painted/coated metal can wear (RAL coatings, a
 *  metal maker's colors). Paint is not offered (2026-10-07, Matt: "only show compatible") */
export function fitsMetalsRole(c: Pick<ColorRecord, 'kind' | 'lines' | 'name'>): boolean {
  return colorFits({ name: 'Painted metal', substrate: 'paintedMetal' }, c).ok
}

/**
 * METALS ROLE (2026-10-07, Matt: "The Metals setting should govern what they say they govern in
 * the Palette settings"): the hex the project's metal items show — gutters, downspouts, railings,
 * flashing. Brushed color, then the finish's project standard, then the palette's Metals color.
 * The Metals color is a deliberate project choice for exactly these items, so it applies whatever
 * KIND of color it is (a Sherwin-Williams paint on a painted-aluminum gutter is field-painted or
 * matched by the fabricator) — only a finish that takes NO color (copper, zinc, glass) keeps its
 * own. `materialId` null = the item's built-in default look, which always takes the role.
 */
export function metalsHex(model: ModelDoc, materialId: string | null | undefined, brushed: unknown): string | null {
  const own = resolveColorRef(model, brushed)
  if (own) return colorToken(own)
  if (materialId) {
    const std = getColorStandards(model)[materialId]
    if (std === NATURAL_REF) return null
    const s = resolveColorRef(model, std)
    if (s) return colorToken(s)
  }
  const rc = getColor(model, getPalette(model).metals)
  if (!rc) return null
  const mat = materialId ? getMaterial(model, materialId as MaterialId) : null
  if (mat && !SUBSTRATES.find((x) => x.value === substrateOf(mat))?.accepts.length) return null
  return colorToken(rc)
}

/**
 * Does a finish take paint (so a role default may color it)? Painted siding, stucco/plaster,
 * gypsum, painted cabinets — never masonry, stone, roofing, metal, glass or a stained wood.
 */
export function paintableFinish(mat: { name?: string; cladding?: { profile?: string } | null; roofing?: unknown } | null | undefined): boolean {
  if (!mat || mat.roofing) return false
  const name = mat.name || ''
  if (/stone|brick|ashlar|ledge|tile|glass|metal|steel|alum|copper|zinc|concrete|cedar|teak|ipe|walnut|oak|stain|natural/i.test(name)) return false
  const prof = mat.cladding?.profile
  if (prof) return prof === 'flat' || prof === 'lap' || prof === 'boardBatten' || prof === 'vertical'
  return /plaster|stucco|gyps|drywall|paint|siding|hardie|fiber ?cement|primed|smooth/i.test(name)
}

/** the renderer's tinted material id: the color's color, the finish's gloss */
// a bare metal's token rides as `metal:<key>:<mat>`; any hex as `tint:<rrggbb>:<mat>`
export const tintMaterialId = (hex: string, materialId: string): string => (hex.startsWith('metal:') ? `${hex}:${materialId}` : `tint:${hex.replace('#', '').toLowerCase()}:${materialId}`)

/** a material id with the surface's color applied (unchanged when there is none) */
export function tinted(materialId: string, hex: string | null | undefined): string {
  return hex ? tintMaterialId(hex, materialId) : materialId
}

/** a short label: 'OC-17 White Dove (Benjamin Moore)' */
export function colorLabel(c: ColorRecord): string {
  return [c.code, c.name].filter(Boolean).join(' ') + (c.maker ? ` (${c.maker})` : '')
}
